/**
 * KAN-656: геометрия кадра фото профиля и вырезка квадрата в JPEG.
 *
 * Модель: картинка w x h (natural), квадратное окно просмотра S px, масштаб
 * zoom >= 1 (1 = cover: меньшая сторона картинки равна S), смещение offset в
 * px окна - сдвиг центра картинки относительно центра окна ({0,0} = по центру).
 * Окно всегда полностью покрыто картинкой: clampOffset не даёт ей уехать.
 */

export interface Offset {
  x: number;
  y: number;
}

/** Квадрат кадра в координатах исходной картинки. */
export interface CropRect {
  x: number;
  y: number;
  size: number;
}

/** Готовый выбор кадра: загруженная картинка и квадрат на ней. */
export interface CropSelection {
  image: HTMLImageElement;
  rect: CropRect;
}

export const MIN_ZOOM = 1;
export const MAX_ZOOM = 3;
export const MAX_OUTPUT_SIZE = 1024;
export const JPEG_QUALITY = 0.92;

/** Базовый коэффициент cover: меньшая сторона картинки равна окну. */
export function fitScale(w: number, h: number, S: number): number {
  const side = Math.min(w, h);
  return side > 0 ? S / side : 1;
}

/** Размер картинки на экране при текущем масштабе. */
function displayed(
  w: number,
  h: number,
  S: number,
  zoom: number
): { k: number; dw: number; dh: number } {
  const k = fitScale(w, h, S) * zoom;
  return { k, dw: w * k, dh: h * k };
}

function clamp(v: number, lo: number, hi: number): number {
  // "+ 0" убирает -0: иначе сравнения через Object.is и вывод в стиле странные.
  return Math.min(hi, Math.max(lo, v)) + 0;
}

/** Смещение, при котором край картинки не заходит внутрь окна. */
export function clampOffset(
  offset: Offset,
  w: number,
  h: number,
  S: number,
  zoom: number
): Offset {
  const { dw, dh } = displayed(w, h, S, zoom);
  const maxX = Math.max(0, (dw - S) / 2);
  const maxY = Math.max(0, (dh - S) / 2);
  return { x: clamp(offset.x, -maxX, maxX), y: clamp(offset.y, -maxY, maxY) };
}

/** Квадрат окна в координатах исходной картинки. */
export function cropRect(
  w: number,
  h: number,
  S: number,
  zoom: number,
  offset: Offset
): CropRect {
  const { k, dw, dh } = displayed(w, h, S, zoom);
  const safe = clampOffset(offset, w, h, S, zoom);
  // Левый верхний угол картинки в координатах окна.
  const left = (S - dw) / 2 + safe.x;
  const top = (S - dh) / 2 + safe.y;
  const size = Math.min(S / k, w, h);
  return {
    x: clamp(-left / k, 0, w - size),
    y: clamp(-top / k, 0, h - size),
    size,
  };
}

/** Сторона итогового JPEG: не больше 1024 и не растягиваем мелкий кадр. */
export function outputSizeFor(rect: CropRect): number {
  return Math.max(1, Math.min(MAX_OUTPUT_SIZE, Math.round(rect.size)));
}

/** Имя файла с расширением .jpg. */
export function jpegName(fileName: string): string {
  const base = fileName.replace(/\.[^.]+$/, '').trim();
  return `${base || 'avatar'}.jpg`;
}

/**
 * Вырезает rect с картинки в квадрат outputSize и кодирует в JPEG 0.92.
 * Прозрачность PNG ложится на белый фон.
 */
export async function cropToFile(
  image: HTMLImageElement,
  rect: CropRect,
  outputSize: number,
  fileName: string
): Promise<File> {
  const side = Math.max(1, Math.round(outputSize));
  const canvas = document.createElement('canvas');
  canvas.width = side;
  canvas.height = side;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Браузер не поддерживает обработку изображений');
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, side, side);
  ctx.drawImage(image, rect.x, rect.y, rect.size, rect.size, 0, 0, side, side);
  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY)
  );
  if (!blob) throw new Error('Не удалось подготовить фото');
  return new File([blob], jpegName(fileName), { type: 'image/jpeg' });
}
